import React, { useState } from "react";
import Container from "../common/Container";
import { HiOutlineSearch, HiOutlineCreditCard, HiOutlineSparkles, HiOutlineGift, HiOutlineLightBulb, HiOutlineShare, HiOutlineCash, HiArrowRight, HiOutlineUserGroup, HiOutlinePencilAlt, HiOutlineCollection, HiOutlineBadgeCheck } from "react-icons/hi";
import { Bars } from "react-loader-spinner";

const processData = {
  backer: {
    title: "For Backers",
    subtitle: "Support groundbreaking research",
    description: "Be a part of the cycle of science development and universal access to knowledge. Fund projects that matter and receive unique NFTs as proof of your contribution.",
    steps: [
      {
        icon: HiOutlineSearch,
        title: "Discover Projects",
        description: "Explore cutting-edge scientific projects across multiple domains. Find research that aligns with your interests and values.",
        color: "from-primary to-[#9c5cff]"
      },
      {
        icon: HiOutlineCreditCard,
        title: "Connect Wallet",
        description: "Securely link your cryptocurrency wallet. We support popular wallets for seamless and safe transactions.",
        color: "from-[#7e7cff] to-primary"
      },
      {
        icon: HiOutlineSparkles,
        title: "Fund Research",
        description: "Contribute to projects that inspire you. Your support helps transform visionary ideas into reality.",
        color: "from-primary-green to-[#0dcada]"
      },
      {
        icon: HiOutlineGift,
        title: "Receive NFT",
        description: "Get exclusive NFTs representing your contribution. Own a piece of scientific history.",
        color: "from-[#0dcada] to-primary-green"
      }
    ]
  },
  researcher: {
    title: "For Researchers",
    subtitle: "Launch your fundraising campaign",
    description: "Embrace the revolutionary fusion of technology and decentralized science. Create your project, attract global backers, and bring your research to life.",
    steps: [
      {
        icon: HiOutlineCreditCard,
        title: "Connect Wallet",
        description: "Start by connecting your crypto wallet to FunDeSci for secure fund management.",
        color: "from-primary to-[#9c5cff]"
      },
      {
        icon: HiOutlineLightBulb,
        title: "Create Project",
        description: "Showcase your research vision with compelling descriptions, goals, and impact potential.",
        color: "from-[#7e7cff] to-primary"
      },
      {
        icon: HiOutlineShare,
        title: "Share & Promote",
        description: "Spread the word across scientific communities and social platforms to attract backers.",
        color: "from-primary-green to-[#0dcada]"
      },
      {
        icon: HiOutlineCash,
        title: "Get Funded",
        description: "Receive contributions from enthusiastic supporters who believe in your research.",
        color: "from-[#0dcada] to-primary-green"
      }
    ]
  },
  community: {
    title: "For Organizations",
    subtitle: "Build your scientific community",
    description: "Create exclusive communities with loyalty NFTs to engage supporters, researchers, and stakeholders. Offer token-gated benefits and build lasting connections.",
    steps: [
      {
        icon: HiOutlineUserGroup,
        title: "Define Community",
        description: "Set up your community with a name, description, and category. Define membership tiers and exclusive perks.",
        color: "from-primary to-[#9c5cff]"
      },
      {
        icon: HiOutlinePencilAlt,
        title: "Design Loyalty NFT",
        description: "Create unique NFT artwork and metadata representing your community membership. Set supply limits and pricing.",
        color: "from-[#7e7cff] to-primary"
      },
      {
        icon: HiOutlineCollection,
        title: "Mint Collection",
        description: "Deploy your NFT collection to the blockchain. Set royalties and configure sale settings for your community.",
        color: "from-primary-green to-[#0dcada]"
      },
      {
        icon: HiOutlineBadgeCheck,
        title: "Grow Membership",
        description: "Members purchase NFTs to join. Grant exclusive access to resources, governance, and community benefits.",
        color: "from-[#0dcada] to-primary-green"
      }
    ]
  }
};

const ProcessSection = ({ handleCreateProject, authLoading }) => {
  const [activeTab, setActiveTab] = useState("backer");
  const data = processData[activeTab];

  return (
    <section className="w-full flex justify-center bg-gradient-to-b from-secondary via-[#1a1230] to-secondary py-24 lg:py-32 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:80px_80px] pointer-events-none"></div>
      <div className="absolute top-1/4 -left-40 w-80 h-80 bg-primary/20 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-primary-green/15 rounded-full blur-[150px] pointer-events-none"></div>
      
      <Container className="w-full">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/10 mb-6">
            <span className="w-2 h-2 bg-primary-green rounded-full animate-pulse"></span>
            <span className="text-white/90 roboto-font text-sm font-medium">How It Works</span>
          </span>
          <h2 className="text-4xl lg:text-5xl xl:text-6xl abel-font text-white mb-6">
            Simple Process, <span className="hero-gradient-text">Powerful Impact</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto roboto-font">
            Whether you're funding breakthrough research or launching your own project, we've made the process seamless.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex justify-center mb-16">
          <div className="inline-flex p-1.5 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10">
            <button
              onClick={() => setActiveTab("backer")}
              className={`px-6 lg:px-8 py-3.5 rounded-xl font-medium transition-all duration-300 text-sm lg:text-base ${
                activeTab === "backer"
                  ? "bg-gradient-to-r from-primary to-[#7e7cff] text-white shadow-lg"
                  : "text-white/60 hover:text-white"
              }`}
            >
              I want to Fund
            </button>
            <button
              onClick={() => setActiveTab("researcher")}
              className={`px-6 lg:px-8 py-3.5 rounded-xl font-medium transition-all duration-300 text-sm lg:text-base ${
                activeTab === "researcher"
                  ? "bg-gradient-to-r from-primary-green to-[#0dcada] text-white shadow-lg"
                  : "text-white/60 hover:text-white"
              }`}
            >
              I'm a Researcher
            </button>
            <button
              onClick={() => setActiveTab("community")}
              className={`px-6 lg:px-8 py-3.5 rounded-xl font-medium transition-all duration-300 text-sm lg:text-base ${
                activeTab === "community"
                  ? "bg-gradient-to-r from-[#7e7cff] to-primary text-white shadow-lg"
                  : "text-white/60 hover:text-white"
              }`}
            >
              Build Community
            </button>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {data.steps.map((step, index) => (
            <div
              key={index}
              className="group relative p-6 lg:p-8 rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:-translate-y-2"
            >
              {/* Step number */}
              <div className="absolute -top-4 -right-2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/40 text-sm font-bold">
                {String(index + 1).padStart(2, '0')}
              </div>
              
              {/* Icon */}
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <step.icon className="w-7 h-7 text-white" />
              </div>
              
              {/* Content */}
              <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
              <p className="text-white/60 leading-relaxed">{step.description}</p>
              
              {/* Connecting line for desktop */}
              {index < 3 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px bg-gradient-to-r from-white/20 to-transparent"></div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <button
            onClick={handleCreateProject}
            className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-primary via-[#9c5cff] to-primary rounded-2xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-primary/30 transition-all duration-300 hover:scale-[1.02]"
          >
            {authLoading ? (
              <Bars height="24" width="24" color="#ffffff" ariaLabel="bars-loading" visible={true} />
            ) : (
              <>
                Get Started Now
                <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </div>
      </Container>
    </section>
  );
};

export default ProcessSection;
